import {
  convertUTMToWGS84,
  convertMultiplePointsToUTM,
  convertMultiplePointsToWGS84,
} from '../../app/components/mepbay/utils/coordinateUtils';

/**
 * @function calculateParabolicCoefficients
 * @description Calculates the parabolic bay-shape model coefficients C0, C1, and C2 from the beta
 * angle, using empirically-fitted quartic polynomials.
 * @param {Number} beta - Beta angle, in degrees.
 * **Called by:
 * @see {calculateWithTurf}
 * @memberof module:Mepbay
 * @returns {Object} {C0, C1, C2} coefficients.
 */
export const calculateParabolicCoefficients = (beta) => {
  const C0 =
    0.0707 +
    -0.0047 * beta +
    0.000349 * Math.pow(beta, 2) +
    -0.00000875 * Math.pow(beta, 3) +
    0.00000004765 * Math.pow(beta, 4);

  const C1 =
    0.9079 +
    0.0125 * beta +
    -0.0006494 * Math.pow(beta, 2) +
    0.00002058 * Math.pow(beta, 3) +
    -0.00000014108 * Math.pow(beta, 4);

  const C2 =
    0.0214 +
    -0.0078 * beta +
    0.0003004 * Math.pow(beta, 2) +
    -0.00001183 * Math.pow(beta, 3) +
    0.00000009343 * Math.pow(beta, 4);

  return { C0, C1, C2 };
};

/**
 * @function calculateVectorAngle
 * @description Calculates the angle between two vectors using the dot product formula.
 * @param {Array<Number>} vector1 - First vector [x, y]
 * @param {Array<Number>} vector2 - Second vector [x, y]
 * @throws {Error} If the computed cosine falls outside [-1, 1].
 * **Called by:
 * @see {calculateWithTurf}
 * @memberof module:Mepbay
 * @returns {Number} Angle in degrees (0-180)
 */
export const calculateVectorAngle = (vector1, vector2) => {
  const dotProduct = vector1[0] * vector2[0] + vector1[1] * vector2[1];

  const magnitude1 = Math.sqrt(Math.pow(vector1[0], 2) + Math.pow(vector1[1], 2));
  const magnitude2 = Math.sqrt(Math.pow(vector2[0], 2) + Math.pow(vector2[1], 2));

  const cosBeta = dotProduct / (magnitude1 * magnitude2);

  if (cosBeta < -1 || cosBeta > 1) {
    throw new Error(`Erro: O valor de cosBeta (${cosBeta}) está fora do intervalo [-1, 1].`);
  }

  const angleRadians = Math.acos(cosBeta);
  const angleDegrees = (angleRadians * 180) / Math.PI;

  return angleDegrees;
};

/**
 * @function calculateRadius
 * @description Calculates the radius R for a given theta using the parabolic model formula:
 * `R = (C0 + C1*(beta/theta) + C2*(beta/theta)^2) * R0`.
 * @param {Number} beta - Beta angle in degrees
 * @param {Number} theta - Theta angle in degrees
 * @param {Number} C0 - Coefficient C0
 * @param {Number} C1 - Coefficient C1
 * @param {Number} C2 - Coefficient C2
 * @param {Number} R0 - Control line length in meters
 * **Called by:
 * @see {generateParabolicPoints}
 * @see {calculateWithTurf}
 * @memberof module:Mepbay
 * @returns {Number} Radius R in meters
 */
export const calculateRadius = (beta, theta, C0, C1, C2, R0) => {
  const ratio = C0 + C1 * (beta / theta) + C2 * Math.pow(beta / theta, 2);
  return ratio * R0;
};

/**
 * @function generateParabolicPoints
 * @description Generates all points along the parabolic bay-shape curve, in UTM, then converts
 * them back to WGS84.
 * 1. Determines the rotation direction (clockwise/counter-clockwise) from the cross product of
 *    the diffraction→closure and diffraction→newPoint vectors.
 * 2. For each theta from `beta` to 150° (1° steps), computes the radius via the parabolic model
 *    and rotates the diffraction→newPoint unit vector by theta to place the curve point.
 * @param {Object} params - Parameters object
 * @param {Object} params.diffractionCoords - Diffraction point in UTM {x, y}
 * @param {Object} params.newPointCoords - New point in UTM {x, y}
 * @param {Object} params.closureCoords - Closure point in UTM {x, y}
 * @param {Number} params.beta - Beta angle in degrees
 * @param {Number} params.C0 - Coefficient C0
 * @param {Number} params.C1 - Coefficient C1
 * @param {Number} params.C2 - Coefficient C2
 * @param {Number} params.R0 - Control line length in meters
 * @param {Number} params.zone - UTM zone number
 * @param {boolean} params.isSouth - Whether points are in southern hemisphere
 * @see {@link calculateRadius}
 * @see {@link convertMultiplePointsToWGS84}
 * **Called by:
 * @see {calculateWithTurf}
 * @memberof module:Mepbay
 * @returns {{points: Array<{lat: Number, lng: Number}>, thetaValues: Array<{theta: Number, radius: Number}>}}
 */
export const generateParabolicPoints = ({
  diffractionCoords,
  newPointCoords,
  closureCoords,
  beta,
  C0,
  C1,
  C2,
  R0,
  zone,
  isSouth,
}) => {
  const thetaIncrement = 1;
  const thetaMax = 150;

  const vectorDN = [newPointCoords.x - diffractionCoords.x, newPointCoords.y - diffractionCoords.y];

  const normVector1 = Math.sqrt(Math.pow(vectorDN[0], 2) + Math.pow(vectorDN[1], 2));
  const unitVector1 = [vectorDN[0] / normVector1, vectorDN[1] / normVector1];

  const vectorFromDiffractionToClosure = [closureCoords.x - diffractionCoords.x, closureCoords.y - diffractionCoords.y];

  const vectorFromDiffractionToNewPoint = [
    newPointCoords.x - diffractionCoords.x,
    newPointCoords.y - diffractionCoords.y,
  ];

  const crossProduct =
    vectorFromDiffractionToNewPoint[0] * vectorFromDiffractionToClosure[1] -
    vectorFromDiffractionToNewPoint[1] * vectorFromDiffractionToClosure[0];

  const rotationDirection = crossProduct <= 0;

  const finalPointsUTM = [];
  const thetaValues = [];

  for (let theta = beta; theta <= thetaMax; theta += thetaIncrement) {
    const radius = calculateRadius(beta, theta, C0, C1, C2, R0);

    let thetaRad = (theta * Math.PI) / 180;

    if (rotationDirection) {
      thetaRad = -thetaRad;
    }

    const rotatedX = unitVector1[0] * Math.cos(thetaRad) - unitVector1[1] * Math.sin(thetaRad);
    const rotatedY = unitVector1[0] * Math.sin(thetaRad) + unitVector1[1] * Math.cos(thetaRad);

    const finalX = diffractionCoords.x + rotatedX * radius;
    const finalY = diffractionCoords.y + rotatedY * radius;

    finalPointsUTM.push({ x: finalX, y: finalY });
    thetaValues.push({
      theta: theta,
      radius: radius,
    });
  }

  const finalPointsWGS84 = convertMultiplePointsToWGS84(finalPointsUTM, zone, isSouth);

  return {
    points: finalPointsWGS84,
    thetaValues: thetaValues,
  };
};

/**
 * @function calculateWithTurf
 * @description Computes the full parabolic bay-shape model (Hsu & Evans) from three control
 * points.
 * 1. Converts the diffraction/closure/tangent points to a common UTM zone, and derives the "new
 *    point" by mirroring the diffraction point across the closure→tangent vector.
 * 2. Computes R0 (control line length) and beta (the angle at the diffraction point), validating
 *    that it's consistent whether measured via the new/closure points or the tangent/closure
 *    points (within a small tolerance).
 * 3. Computes the C0/C1/C2 parabolic coefficients for that beta, and sanity-checks that the
 *    radius/angle at theta=beta match R0/beta.
 * 4. Generates the full parabolic curve's points and returns every intermediate result.
 * @param {Object} diffPoint - Diffraction point {lat, lng}
 * @param {Object} clsPoint - Closure point {lat, lng}
 * @param {Object} tngPoint - Tangent point {lat, lng}
 * @see {@link convertMultiplePointsToUTM}
 * @see {@link calculateVectorAngle}
 * @see {@link calculateParabolicCoefficients}
 * @see {@link calculateRadius}
 * @see {@link generateParabolicPoints}
 * @throws {Error} If the two beta measurements disagree, or the theta=beta sanity check fails.
 * **Called by:
 * @see {ProcessingPage} - MEPBay module's processing page (imported as ProcessingMepBayPage).
 * @memberof module:Mepbay
 * @returns {Object} {beta, cosBeta, cosBetaConf, C0, C1, C2, R0, newPointWGS84, parabolaPoints, thetaValues, zone, isSouth}
 */
export const calculateWithTurf = (diffPoint, clsPoint, tngPoint) => {
  try {
    const { utmPoints, zone, isSouth } = convertMultiplePointsToUTM([diffPoint, clsPoint, tngPoint]);

    const [diffractionCoords, closureCoords, tangentCoords] = utmPoints;

    const dx = tangentCoords.x - closureCoords.x;
    const dy = tangentCoords.y - closureCoords.y;

    const newPointCoords = {
      x: diffractionCoords.x - dx,
      y: diffractionCoords.y - dy,
    };

    const newPointWGS84 = convertUTMToWGS84(newPointCoords.x, newPointCoords.y, zone, isSouth);

    const dx_R0 = closureCoords.x - diffractionCoords.x;
    const dy_R0 = closureCoords.y - diffractionCoords.y;
    const R0 = Math.sqrt(dx_R0 * dx_R0 + dy_R0 * dy_R0);

    const vector1 = [newPointCoords.x - diffractionCoords.x, newPointCoords.y - diffractionCoords.y];

    const vector2 = [closureCoords.x - diffractionCoords.x, closureCoords.y - diffractionCoords.y];

    const betaNewPoint = calculateVectorAngle(vector1, vector2);

    const vector3 = [diffractionCoords.x - closureCoords.x, diffractionCoords.y - closureCoords.y];

    const vector4 = [tangentCoords.x - closureCoords.x, tangentCoords.y - closureCoords.y];

    const betaTangentPoint = calculateVectorAngle(vector3, vector4);

    const tolerance = 1e-6;
    const betaDifference = Math.abs(betaNewPoint - betaTangentPoint);

    if (betaDifference > tolerance) {
      throw new Error(`Os ângulos beta não são iguais. Diferença: ${betaDifference}. ` + `O código foi interrompido.`);
    }

    const beta = betaNewPoint;

    const dotProduct1 = vector1[0] * vector2[0] + vector1[1] * vector2[1];
    const magnitude1 = Math.sqrt(vector1[0] ** 2 + vector1[1] ** 2);
    const magnitude2 = Math.sqrt(vector2[0] ** 2 + vector2[1] ** 2);
    const cosBeta = dotProduct1 / (magnitude1 * magnitude2);

    const dotProduct2 = vector3[0] * vector4[0] + vector3[1] * vector4[1];
    const magnitude3 = Math.sqrt(vector3[0] ** 2 + vector3[1] ** 2);
    const magnitude4 = Math.sqrt(vector4[0] ** 2 + vector4[1] ** 2);
    const cosBetaConf = dotProduct2 / (magnitude3 * magnitude4);

    const { C0, C1, C2 } = calculateParabolicCoefficients(beta);

    const conference = C0 + C1 + C2;
    const roundedConference = Math.round(conference * 10000) / 10000;

    const firstTheta = beta;
    const firstRadius = calculateRadius(beta, firstTheta, C0, C1, C2, R0);

    const thetaMatch = Math.abs(firstTheta - beta) <= tolerance;
    const radiusMatch = Math.abs(firstRadius - R0) <= tolerance;

    if (!thetaMatch) {
      throw new Error('>>>> Os ângulos beta NÃO são iguais. <<<<<');
    }

    if (!radiusMatch) {
      throw new Error('>>>> O comprimento de reta NÃO está dentro da tolerância <<<<<');
    }

    const { points: parabolaPoints, thetaValues } = generateParabolicPoints({
      diffractionCoords,
      newPointCoords,
      closureCoords,
      beta,
      C0,
      C1,
      C2,
      R0,
      zone,
      isSouth,
    });

    return {
      beta: beta,
      cosBeta: cosBeta,
      cosBetaConf: cosBetaConf,
      C0: C0,
      C1: C1,
      C2: C2,
      R0: R0,
      newPointWGS84: newPointWGS84,
      parabolaPoints: parabolaPoints,
      thetaValues: thetaValues,
      zone: zone,
      isSouth: isSouth,
    };
  } catch (error) {
    console.error('Erro ao calcular modelo parabólico com Turf.js:', error);
    throw error;
  }
};
